import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useTable } from 'react-table';
import './HostelRequestCallBack.css';
import HeaderMenu from '../Header/HeaderMenu';
import checkPermission from '../Utils/checkPermission';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert
} from '@mui/material';
import axiosApi from '../Utils/axiosApi';

const HostelRequestCallBack = () => {
  const [visits, setVisits] = useState([]);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hostelList, setHostelList] = useState([]);
  const [pendingHostelFilter, setPendingHostelFilter] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const formatDateTimeLocal = (dateTime) => {
    if (!dateTime) return '';
    const date = new Date(dateTime);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().slice(0, 16);
  };

  // Fetch hostel list
  useEffect(() => {
    axiosApi
      .post('/hostel/getHostelName', {})
      .then((response) => {
        const data = Array.isArray(response.data)
          ? response.data
          : response.data.hostels || [];
        const formatted = data.map((item) =>
          Array.isArray(item)
            ? { id: item[0], hostelName: item[1] }
            : { id: item.id, hostelName: item.name }
        );
        setHostelList(formatted);
      })
      .catch((error) => console.error('Error fetching hostels:', error));
  }, []);

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosApi.post(
        '/hostelRequestCallback/getHostelRequestCallback',
        {}
      );
      const data = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];
      setVisits(data);
    } catch (error) {
      console.error('Error fetching data:', error);
      setVisits([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleResetFilters = () => {
    setPendingHostelFilter('');
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      const selectedHostel = hostelList.find(
        (h) => h.hostelName === pendingHostelFilter
      );
      const requestBody = {
        hostelIds: selectedHostel?.id ? [selectedHostel.id] : []
      };
      const response = await axiosApi.post(
        `/hostelRequestCallback/getHostelRequestCallback`,
        requestBody
      );
      const bookings = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];
      setVisits(bookings);
    } catch (error) {
      console.error('Error fetching filtered bookings:', error);
      setVisits([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ useCallback to stabilize the function
  const handleManageRequest = useCallback((hostelRequestCallback) => {
    axiosApi
      .post(`/hostelRequestCallback/getHostelRequestCallback`, {
        hostelRequestCallbackIds: [hostelRequestCallback.id],
      })
      .then((response) => {
        const details =
          Array.isArray(response.data) && response.data.length > 0
            ? response.data[0]
            : response.data;
        setSelectedVisit({
          ...details,
          respondedDateAndTime: formatDateTimeLocal(details.respondedDateAndTime),
        });
        setSuccessMessage('');
        setErrorMessage('');
        setDialogOpen(true);
      })
      .catch((err) => {
        console.error('Error fetching hostelRequestCallback details:', err);
      });
  }, []);

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedVisit(null);
  };

  const handleUpdate = async () => {
    try {
      const payload = {
        hostelRequestCallbackDTO: {
          id: selectedVisit.id,
          callbackStatus: selectedVisit.callbackStatus,
          remarks: selectedVisit.remarks || '',
          respondedDateAndTime: selectedVisit.respondedDateAndTime || null
        }
      };
      await axiosApi.post(
        '/hostelRequestCallback/updateHostelRequestCallback',
        payload
      );
      await fetchData();
      setSuccessMessage('Request updated successfully.');
      setErrorMessage('');
    } catch (error) {
      console.error('Error updating hostelRequestCallback:', error);
      setErrorMessage('Failed to update request.');
      setSuccessMessage('');
    }
  };

  // ✅ useCallback to stabilize delete handler
  const handleDeleteClick = useCallback((id) => {
    setDeleteConfirmId(id);
    setDeleteDialogOpen(true);
  }, []);

  const confirmDelete = async () => {
    try {
      await axiosApi.post(
        '/hostelRequestCallback/deleteHostelRequestCallback',
        { hostelRequestCallbackIds: [deleteConfirmId] }
      );
      setDeleteDialogOpen(false);
      setDeleteConfirmId(null);
      await fetchData();
      setSuccessMessage('Request deleted successfully.');
      setErrorMessage('');
    } catch (error) {
      console.error('Error deleting request:', error);
      setErrorMessage('Failed to delete request.');
    }
  };

  const handleFieldChange = (field, value) => {
    setSelectedVisit((prev) => ({ ...prev, [field]: value }));
  };

  // ✅ useMemo now has stable dependencies
  const columns = useMemo(
    () => [
      { Header: '#', accessor: '' },
      { Header: 'Hostel Name', accessor: 'hostelName' },
      { Header: 'Client Name', accessor: 'requesterName' },
      { Header: 'Mobile Number', accessor: 'mobileNumber' },
      { Header: 'Status', accessor: 'callbackStatus' },
      {
        Header: 'Actions',
        accessor: 'actions',
        Cell: ({ row }) => (
          <div className="actions-container">
            <Button disabled={!checkPermission("Hostel", "Schedule CallBack", "VIEW")}
              size="small"
              color="primary"
              onClick={() => handleManageRequest(row.original)}
            >
              Manage
            </Button>
            <Button disabled={!checkPermission("Hostel", "Schedule CallBack", "DELETE")}
              size="small"
              color="secondary"
              onClick={() => handleDeleteClick(row.original.id)}
            >
              Delete
            </Button>
          </div>
        ),
      },
    ],
    [handleManageRequest, handleDeleteClick]
  );

  const tableInstance = useTable({
    columns,
    data: visits
  });

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    tableInstance;

  return (
    <div>
      <HeaderMenu />
      <div className="hostel-request-callback-container">
        <h2>Hostel Request Callback</h2>

        {/* Filter Bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            backgroundColor: '#f9f9f9',
            p: 2,
            borderRadius: 2,
            mb: 3,
            boxShadow: 1,
            flexWrap: 'nowrap'
          }}
        >
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Hostel Name</InputLabel>
            <Select
              value={pendingHostelFilter}
              onChange={(e) => setPendingHostelFilter(e.target.value)}
              label="Hostel Name"
            >
              <MenuItem value="">All</MenuItem>
              {hostelList.map((hostel) => (
                <MenuItem key={hostel.id} value={hostel.hostelName}>
                  {hostel.hostelName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button variant="contained" size="small" onClick={handleSearch}>
            Search
          </Button>

          <Button
            variant="outlined"
            size="small"
            color="secondary"
            onClick={handleResetFilters}
          >
            Reset
          </Button>
        </Box>

        {loading ? (
          <div className="loading-container">
            <CircularProgress />
            <p>Loading requests...</p>
          </div>
        ) : (
          <div className="table-container">
            <table {...getTableProps()} className="table">
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                    {headerGroup.headers.map((column) => (
                      <th {...column.getHeaderProps()} key={column.id}>
                        {column.render('Header')}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {rows.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()} key={row.id}>
                      {row.cells.map((cell) => (
                        <td
                          {...cell.getCellProps()}
                          key={cell.column.id}
                          data-label={cell.column.Header}
                        >
                          {cell.render('Cell')}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Manage Dialog */}
        {isDialogOpen && selectedVisit && (
          <Dialog
            open={isDialogOpen}
            onClose={(e, r) => {
              if (r === 'backdropClick' || r === 'escapeKeyDown') return;
              handleCloseDialog();
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Manage Request Callback</DialogTitle>
            <DialogContent>
              {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
              {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}
              <div className="dialog-grid">
                <div><label>Name:</label><input type="text" value={selectedVisit.requesterName || ''} readOnly /></div>
                <div><label>Email:</label><input type="text" value={selectedVisit.email || ''} readOnly /></div>
                <div><label>Phone Number:</label><input type="text" value={selectedVisit.mobileNumber || ''} readOnly /></div>
                <div><label>Requester Location:</label><input type="text" value={selectedVisit.requesterLocation || ''} readOnly /></div>
                <div><label>Responded Date & Time:</label><input type="datetime-local" value={selectedVisit.respondedDateAndTime || ''} onChange={(e) => handleFieldChange('respondedDateAndTime', e.target.value)} /></div>
                <div><label>Requester Query:</label><input type="text" value={selectedVisit.requesterQuery || ''} readOnly /></div>
                <div>
                  <label>Status:</label>
                  <select value={selectedVisit.callbackStatus || 'PENDING'} onChange={(e) => handleFieldChange('callbackStatus', e.target.value)}>
                    <option value="PENDING">Pending</option>
                    <option value="COMPLETED">Completed</option>
                    <option value="CANCELLED">Canceled</option>
                  </select>
                </div>
                <div style={{ gridColumn: 'span 1' }}>
                  <label>Remarks:</label>
                  <textarea value={selectedVisit.remarks || ''} onChange={(e) => handleFieldChange('remarks', e.target.value)} placeholder="Enter remarks..." />
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <Button disabled={!checkPermission("Hostel", "Schedule CallBack", "UPDATE")} variant="contained" onClick={handleUpdate}>Update</Button>
              <Button variant="outlined" onClick={handleCloseDialog}>Close</Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Delete Confirmation Dialog */}
        {isDeleteDialogOpen && (
          <Dialog
            open={isDeleteDialogOpen}
            onClose={() => setDeleteDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
              <p>Are you sure you want to delete this request?</p>
            </DialogContent>
            <DialogActions>
              <Button variant="contained" color="error" onClick={confirmDelete}>Yes, Delete</Button>
              <Button variant="outlined" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            </DialogActions>
          </Dialog>
        )}
      </div>
    </div>
  );
};

export default HostelRequestCallBack;
